'use client'

import * as React from 'react'

import { Breadcrumbs } from '@/components/breadcrumbs'
import { CopperplateHatch } from '@/components/ui/copperplate-hatch'
import { Button } from '@/components/ui/button'
import { ChromaText } from '@/components/ui/chroma-text'
import { SOLUTIONS } from '@/config/solutions'
import { cn } from '@/lib/utils'

/* Cloud Infrastructure hero — an engraved plate.

   Replaces the several-MB hero_cloud.mp4 loop with a canvas the page can
   afford: one rAF loop that pauses offscreen, and a single still frame under
   reduced motion.

   The plate paints itself in the theme's own `--background`, so it reads as
   texture on the page rather than a slab dropped onto it, and it can sit under
   the transparent header like every other hero here.

   All copy is read from @/config/solutions, the same source the rest of the
   page reads, so the hero can never drift from it. */

const solution = SOLUTIONS['cloud-infrastructure']!

/** Brand mint, so the burin strokes glint in Euclideum's accent rather than the
    component's default candle-gold. */
const GLINT = '#37ecb0'

/** Below Tailwind's `lg` the copy owns the full width, so the desktop
    composition (quiet left column, light field on the right) has nowhere to
    live — the plate recomposes instead of squeezing. Reads synchronously on
    the client; the value never renders into markup, so no hydration risk. */
function useCompactPlate(): boolean {
  const [compact, setCompact] = React.useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 1023px)').matches
  )
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)')
    setCompact(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setCompact(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return compact
}

export function CloudHero() {
  const compact = useCompactPlate()

  return (
    <section
      className={cn(
        'relative overflow-hidden bg-background',
        'mt-[calc(var(--header-h)*-1)]'
      )}
    >
      <CopperplateHatch
        className="w-full"
        density={1.05}
        /* The plate should read as an engraved surface across its whole width,
           not a patch of ink around the light. Compact plates carry a little
           less ink so the texture stays texture on a small screen. */
        coverage={compact ? 1.1 : 1.25}
        intensity={compact ? 0.85 : 0.95}
        speed={0.85}
        /* On touch, a stray drag would yank the light up behind the copy —
           keep the compact composition still. */
        interactive={!compact}
        seed={7}
        accent={GLINT}
        /* Desktop: anchored to the copy column so the plate quietens exactly
           where the text sits and keeps its texture out to the right.
           Compact: the copy spans the full width, so the quiet zone covers the
           whole column and the texture lives at the foot of the plate. */
        safeArea={
          compact
            ? { x: 0, y: 0, w: 1, h: 0.78 }
            : { x: 0.03, y: 0.16, w: 0.5, h: 0.68 }
        }
        focalPoint={
          compact
            ? [{ x: 0.5, y: 1.02 }]
            : [
                { x: 0.7, y: 0.44 },
                { x: 0.9, y: 0.72 },
              ]
        }
      >
        <div className="container mx-auto">
          {/* Below lg the light lives at the foot of the plate, so the column
              keeps a deep foot for it to glow in; on desktop the prose section
              below brings its own generous top padding, so the plate only
              needs enough foot to not crowd the buttons. */}
          <div className="flex flex-col justify-center pb-36 pt-[calc(var(--header-h)+3.5rem)] sm:pt-[calc(var(--header-h)+4.5rem)] lg:pb-14">
            <Breadcrumbs
              className="mb-5"
              items={[
                { name: 'Home', href: '/' },
                { name: 'Solutions', href: '/solutions' },
                { name: solution.title, href: `/solutions/${solution.slug}` },
              ]}
            />

            <h1
              className={cn(
                'font-heading max-w-4xl font-normal tracking-tight text-foreground',
                'text-[34px]/[1.12] sm:text-5xl/[1.12] md:text-6xl/[1.1] lg:text-[64px]/[1.08]'
              )}
            >
              <ChromaText>{solution.heading ?? solution.title}</ChromaText>
            </h1>

            <p className="font-heading mt-4 max-w-3xl text-xl font-normal leading-snug text-foreground/80 sm:text-2xl md:text-[28px]">
              {solution.subtitle}
            </p>

            {/* The one line that carries the argument, on a hairline. */}
            <p className="mt-7 max-w-xl border-l border-border pl-5 text-base leading-relaxed text-emerald-600 sm:text-lg dark:text-primary-active">
              {solution.tagline}
            </p>

            <div className="mt-9 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button asChild size="xl">
                <a href="/schedule-meeting">Talk to us</a>
              </Button>

              <Button variant="outline" asChild size="xl">
                <a href="#delivers">What it delivers</a>
              </Button>
            </div>
          </div>
        </div>
      </CopperplateHatch>
    </section>
  )
}
